import { DynamicModal } from "src/components/base/components/DynamicModal";
import { createState } from "src/utils/create_zustand_store";
import { useRouterExt } from "../../hooks/useRouterExt";
import { routes } from "src/shared/routes";
import { Box, Grid } from "@mui/material";
import React from "react";
import { TPass, TPassOnAccount } from "src/shared/types/globalStotre.types";
import { PassCard } from "../../../feature/PurchaseFlow/PassCard";
import { checkoutTabs } from "src/components/feature/PurchaseFlow/usePurchaseFlow";
import { throttleTime } from "src/utils/utils";
import { useLogout } from "../../hooks/useLogout";
import { useGlobalStore } from "../../hooks/useGlobalStore";
import { constants } from "src/shared/constants";
import { useApiRoutes } from "../../hooks/useApiRoutes";
import { toast } from "react-toastify";
import { findApiErrorMessage } from "../../utils/utils";
import { useTeamConfig } from "../TeamConfigProvider";
import { usePassFlow } from "src/components/feature/PurchaseFlow/usePassFlow";
import { PassGroupCardContainer } from "src/components/feature/common/PassGroupCardContainer";
import { SSButton } from "src/components/base/components/SSButton";

export const usePassPurchaseModal = createState({
  isOpen: false,
  passes: [] as TPass[],
  onConfirm: async () => {},
});

let isOAuthStateChecked = false;

export function PassPurchaseModal() {
  const router = useRouterExt();
  const { handleLogout } = useLogout();
  const modalState = usePassPurchaseModal();
  const urlCode = router?.query?.urlCode as string;
  const inviteeUserId = router?.query?.inviteeUserId as string;
  const { onClickSubscribe, onClickSendAsGift } = usePassFlow();
  const { user, oAuthState, setGlobalState } = useGlobalStore();
  const { getPassesOnAccountApi, getPassesApi } = useApiRoutes();
  const { TEAM_GLOBAL_SETTINGS } = useTeamConfig();

  const handleCloseAndLogout = () => {
    handleLogout();
    modalState.set({ isOpen: false });
    throttleTime(100, () => modalState.reset());
  };

  const handleSubscribeAndClose = async (passId: number) => {
    onClickSubscribe(passId);
    modalState.set({ isOpen: false });
    throttleTime(100, () => {
      modalState.reset();
    });
  };

  const handleSendAsGift = async (passId: number) => {
    onClickSendAsGift(passId);
    modalState.set({ isOpen: false });
    throttleTime(100, () => {
      modalState.reset();
    });
  };

  const handlePassGroupPurchase = async () => {
    router.pushSchema({
      pathname: routes.checkout,
      query: { tab: checkoutTabs.checkout },
    });

    modalState.set({ isOpen: false });
    throttleTime(100, () => {
      modalState.reset();
    });
  };

  const getPassesAndFireModal = async (urlCode?: string) => {
    try {
      const passesOnAccount = await getPassesOnAccountApi.get();
      setGlobalState({ passesOnAccount });
      const params: Record<string, any> = {};
      if (urlCode) params.queryParams = { urlCode };
      if (inviteeUserId) {
        params.queryParams = { ...params.queryParams, inviteeUserId };
      }
      const passes = await getPassesApi.get(params);
      if (passesOnAccount?.length === 0 && !modalState.isOpen) {
        modalState.set({ isOpen: true, passes });
      }
    } catch (err) {
      console.log(err);
      toast.error(findApiErrorMessage(err));
    }
  };

  // prettier-ignore
  const handleCheckPassOnAccount = async () => {
    if(TEAM_GLOBAL_SETTINGS?.DISABLE_PASS_ON_ACCOUNT_RESTRICTION) return;
    
    const { authOrigins } = constants;
    const isPurchaseFlow = oAuthState?.authOrigin == authOrigins.purchasePlan; 
    const isGroupInvitePurchaseFlow = oAuthState?.authOrigin == authOrigins.groupInvitePurchase; 
    const isLandingPage = router.pathname === routes.landing;
    const isCheckoutPage = router.pathname === routes.checkout;
    const isCheckoutWithPassId = Boolean(router.query.passId); 
    const isBallparkAppGroupInvite = TEAM_GLOBAL_SETTINGS?.IS_BALLPARK_APP  && router.pathname === routes.group_invite;
    const isPassGroupCheckout = TEAM_GLOBAL_SETTINGS?.IS_PASS_GROUP_ENABLED && router.pathname === routes.checkout;
    const isFaqsPage = router.pathname === routes.faqs;
    const isOffersPage = router.pathname === routes.offers || router.pathname === routes.offerId;
    const isMarketplacePage = router?.pathname?.includes('marketplace');
    const isUpgradesPage = router.pathname === routes.upgrades;
    const isRedeemGiftPage = router.pathname === routes.redeem_gift;

    const urlCode = router.query.urlCode as string;
    
    if (oAuthState && !isOAuthStateChecked) {
      isOAuthStateChecked = true;
      if (isPurchaseFlow  || isGroupInvitePurchaseFlow || isLandingPage || isCheckoutPage || isCheckoutWithPassId || isBallparkAppGroupInvite || isPassGroupCheckout || isFaqsPage || isOffersPage || isMarketplacePage || isUpgradesPage || isRedeemGiftPage) return;
      return await getPassesAndFireModal(urlCode);
    }
    
    if (isOAuthStateChecked || (!oAuthState && !isOAuthStateChecked)) {
      if (isLandingPage || isCheckoutWithPassId || isBallparkAppGroupInvite || isPassGroupCheckout || isFaqsPage || isOffersPage || isMarketplacePage || isUpgradesPage || isRedeemGiftPage) return;
      return await getPassesAndFireModal(urlCode);
    }
  };

  React.useEffect(() => {
    if (!user) return;
    handleCheckPassOnAccount();
  }, [oAuthState, router]);

  if (
    TEAM_GLOBAL_SETTINGS?.IS_PASS_GROUP_ENABLED &&
    TEAM_GLOBAL_SETTINGS?.PASS_GROUP_CARDS?.length
  ) {
    return (
      <DynamicModal
        isOpen={modalState.isOpen}
        title="Finish Purchase"
        onClose={handleCloseAndLogout}
        renderBody={() => {
          return (
            <Box py={1}>
              {TEAM_GLOBAL_SETTINGS?.IS_PASS_GROUP_ENABLED &&
              TEAM_GLOBAL_SETTINGS?.PASS_GROUP_CARDS?.length ? (
                <PassGroupCardContainer
                  cardProps={{
                    onClickPurchase: handlePassGroupPurchase,
                    isPurchaseButtonHidden: true,
                    slot: {
                      wrapper: {
                        sx: {
                          border: "unset",
                        },
                      },
                    },
                  }}
                />
              ) : null}
            </Box>
          );
        }}
        renderFooter={() => {
          return (
            <Box p={2}>
              <SSButton
                fullWidth
                size="large"
                onClick={handlePassGroupPurchase}
              >
                Purchase Now
              </SSButton>
            </Box>
          );
        }}
      />
    );
  }

  return (
    <DynamicModal
      isOpen={modalState.isOpen}
      title="Finish Purchase"
      description={
        <Box py={1}>
          <Grid container spacing={3} justifyContent={"center"}>
            {modalState.passes.map((itm) => {
              return (
                <Grid key={itm.passId} item xs={12} md={6}>
                  <PassCard
                    variant="variant1"
                    hideImage={true}
                    pass={itm}
                    onClickSubscribe={() => handleSubscribeAndClose(itm.passId)}
                    onClickSendAsGift={() => handleSendAsGift(itm.passId)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      }
      onClose={handleCloseAndLogout}
      renderFooter={() => <></>}
    />
  );
}
